import React, { useContext, useState } from 'react';
import { Box, Typography, FormControlLabel } from '@mui/material';
import BulletList from './bullet-list';
import NumberList from './number-list';
import Model3DViewer from './model3DViewer';
import Callout from './callout';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageManager from './handout-image-manager';
import HandoutImageLoader from './handout-image-loader';
import IconStrip from './icon-strip';
import Copyright from './copyright';
import { GlobalStyles } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { toJS } from 'mobx';
import AntSwitch from '../../UIControls/AntSwitch';
import DisclaimerNotice from './disclaimer-notice';

const PATIENT_NAME_LABEL = 'Patient name';
const CLIENT_NAME_LABEL = 'Client name';

function DisplayHandout({
    handoutData,
    clinicData,
    show3DModel = true,
    showMainIllustration = true,
    showTutorialVideo = true,
    clinicName = '',
    partnerUserData,
}) {
    console.warn('displayHandout handoutData', toJS(handoutData));

    const { imageStore } = useContext(StoreContext);
    const theme = useTheme();

    const locale = 'en-UK';
    const localeData = handoutData.contentData[locale];
    const [showLabels, setShowLabels] = useState(true);

    const isSharedWhiteboard = !!handoutData.whiteboardId;
    const isCustomisedHandout = !!handoutData.customisedId;
    const showDisclaimer = isSharedWhiteboard || isCustomisedHandout;
    const vetName = showDisclaimer
        ? `${handoutData.updatedByFirstName || ''} ${handoutData.updatedByLastName || ''}`.trim()
        : '';

    const patientDetails = handoutData.patientDetails || {};
    const sharingMetadata = handoutData.sharingMetadata || {};

    const patientName = handoutData.patientName || patientDetails.patientName || '';
    const clientSurname = handoutData.clientSurname || patientDetails.clientSurname || '';
    const patientSurname = handoutData.patientSurname || patientDetails.patientSurname || '';
    const updatedByFirstName = handoutData.updatedByFirstName || sharingMetadata.updatedByFirstName || '';
    const updatedByLastName = handoutData.updatedByLastName || sharingMetadata.updatedByLastName || '';
    const sharedAt = handoutData.sharedAt || sharingMetadata.sharedAt || handoutData.lastUpdated || null;

    // Format sharing metadata
    const formatDateTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const givenByName =
        updatedByFirstName && updatedByLastName ? `${updatedByFirstName} ${updatedByLastName}` : '';

    // Determine if this is Physio (patientSurname) or Vet (clientSurname)
    const isPhysio = Boolean(patientSurname);
    const isVet = Boolean(clientSurname);

    // For Physio: combine patientName + patientSurname (same person)
    // For Vet: keep separate patientName and clientSurname fields for backend compatibility.
    const fullPatientName = isPhysio && patientName && patientSurname
        ? `${patientName} ${patientSurname}`
        : patientName;

    const hasPatientDetails = Boolean(fullPatientName || clientSurname || givenByName || sharedAt);
    const patientDetailRows = [
        {
            left: fullPatientName ? `${PATIENT_NAME_LABEL}: ${fullPatientName}` : '',
            right: isVet && clientSurname ? `${CLIENT_NAME_LABEL}: ${clientSurname}` : '',
        },
        {
            left: sharedAt ? `Date: ${formatDateTime(sharedAt)}` : '',
            right: givenByName ? `Given by: ${givenByName}` : '',
        },
    ].filter((row) => row.left || row.right);

    let mainIllustrationRendered = false;

    if (!localeData) {
        throw new Error(`No data found for the specified locale: ${locale}`);
    }

    // Diagram labels are controlled from the document title, even though the diagram image is a later section.
    const shouldShowDiagramToggle = !isSharedWhiteboard && localeData.some((section) => section.sectionName === 'diagram');

    return (
        <HandoutImageManager handoutData={handoutData}>
            <GlobalStyles
                styles={{
                    '@media print': {
                        // The content should have a bottom padding equal to or larger than the footer height
                        // to prevent content from overlapping with the footer
                        '#handout-content': {
                            paddingBottom: '100px', // Adjust based on the actual height of your footer
                        },
                    },
                }}
            />
            <Box id="handout-content" sx={{ my: 0, position: 'relative' }}>
                {localeData.map((section, index) => generateSection(section, index))}
            </Box>
            {showDisclaimer && (
                <Box sx={{ mb: 0 }}>
                    <DisclaimerNotice vetName={vetName} clinicName={clinicData?.clinicName || ''} />
                </Box>
            )}
            <Copyright clinicData={clinicData} />
        </HandoutImageManager>
    );

    function generateSection(jsonContent, index) {
        const sectionName = jsonContent.sectionName;
        const isPrintPageBreak = sectionName === 'printPageBreak';
        // Handle 'diagram' section specially to toggle between labeled and unlabeled images
        let blocks = sectionName === 'diagram' ? [jsonContent.blocks[showLabels ? 0 : 1]] : jsonContent.blocks;

        // added filtering to catch toggle states
        blocks = blocks.filter((block) => {
            if (block.type === '3dModel' && !show3DModel) return false;
            if (block.type === 'illustration' && !showMainIllustration) return false;
            if (block.type === 'tutorialVideo' && !showTutorialVideo) return false;
            return true;
        });

        // If no blocks remain after filtering, skip rendering this section
        if (blocks.length === 0) {
            return null;
        }

        return (
            <React.Fragment key={index}>
                <Box
                    key={jsonContent.sectionName}
                    className={`${sectionName}${isPrintPageBreak ? ' printPageBreak' : ''}`}
                    component="section"
                    sx={{
                        my: 5,
                        '@media print': {
                            // pageBreakInside: 'avoid',
                            // pageBreakAfter: 'auto', // This will add a break after the section if needed
                            // marginBottom: '30px', // Adjust this value as needed for proper spacing
                        },
                    }}
                >
                    {blocks.map((block, index) => {
                        const isMainIllustration = block?.type === 'illustration' && !mainIllustrationRendered;
                        if (isMainIllustration) {
                            mainIllustrationRendered = true;
                        }
                        return (
                            <React.Fragment key={index}>
                                {blockToComponentMapping(block, isMainIllustration, shouldShowDiagramToggle)}
                            </React.Fragment>
                        );
                    })}
                </Box>
                {isPrintPageBreak && <div></div>}
            </React.Fragment>
        );
    }

    function blockToComponentMapping(block, isMainIllustration, shouldShowDiagramToggle = false) {
        if (!block || !block.type) {
            console.error(`Block is missing type or is undefined`, block);
            return null;
        }
        switch (block.type) {
            case 'documentTitle':
                return (
                    <React.Fragment key={block.value}>
                        <Box
                            sx={{
                                mb: 2,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: 1,
                            }}
                            className={`${block.type}`}
                        >
                            <Typography
                                component="h1"
                                variant="documentTitle"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflowWrap: 'break-word',
                                }}
                            >
                                {block.value}
                            </Typography>
                            {shouldShowDiagramToggle && (
                                <Box sx={{ flexShrink: 0, pb: '4px' }}>
                                    <FormControlLabel
                                        control={
                                            <AntSwitch
                                                checked={showLabels}
                                                onChange={() => setShowLabels((prev) => !prev)}
                                            />
                                        }
                                        label="Labels"
                                        labelPlacement="start"
                                        sx={{ '& .MuiFormControlLabel-label': { marginRight: '8px' } }}
                                    />
                                </Box>
                            )}
                        </Box>
                        {hasPatientDetails && (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1,
                                    backgroundColor: theme.palette.background.paper,
                                    borderRadius: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                    }}
                                >
                                    {patientDetailRows.map((row, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: 'repeat(2, minmax(120px, 1fr))',
                                                },
                                                columnGap: 3,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#000453', fontWeight: 500, lineHeight: 1.4 }}
                                            >
                                                {row.left}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#000453', fontWeight: 500, lineHeight: 1.4 }}
                                            >
                                                {row.right}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </React.Fragment>
                );

            case 'sectionTitle':
                return (
                    <Typography key={block.value} variant="sectionTitle" sx={{ mb: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );
            case 'sectionSubtitle':
                return (
                    <Typography key={block.value} variant="sectionSubtitle" sx={{ mb: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );
            case 'calloutSmall':
                return (
                    <Callout key={block.value} className={`${block.type}`}>
                        {block.value}
                    </Callout>
                );
            case 'calloutMedium':
                return (
                    <Callout key={block.value} className={`${block.type}`}>
                        {block.value}
                    </Callout>
                );
            case 'paragraph':
                return (
                    <Typography key={block.value} sx={{ my: 2 }} className={`${block.type}`}>
                        {block.value}
                    </Typography>
                );
            case 'list':
                return <BulletList items={block.values} className={`${block.type}`} />;
            case 'numberList':
                return <NumberList items={block.values} />;
            case 'iconStrip':
                return (
                    <IconStrip iconDetails={block.iconDetails} imageStore={imageStore} className={`${block.type}`} />
                );
            case 'illustration':
                const shouldUseWhiteboard =
                    isMainIllustration && isSharedWhiteboard && handoutData.canvasImageLink;
                const shouldUseAttachedWhiteboard =
                    isMainIllustration && isCustomisedHandout && handoutData.attachedWhiteboardCanvasImageLink;
                const imageSrc = shouldUseWhiteboard
                    ? handoutData.canvasImageLink
                    : shouldUseAttachedWhiteboard
                      ? handoutData.attachedWhiteboardCanvasImageLink
                      : block.src;
                return (
                    <HandoutImageLoader
                        src={imageSrc}
                        alt={block.alt}
                        imageStore={imageStore}
                        className={`${block.type}`}
                    />
                );
            case '3dModel': // 3D Model thing!
                return (
                    <Model3DViewer uid={block.uid} alt={block.alt} author={block.author} authorWeb={block.authorWeb} />
                );
            case 'printPageBreak':
                return (
                    <>
                        <div className="printPageBreak"></div>
                        <p></p>
                    </>
                );
            default:
                return null;
        }
    }
}

export default DisplayHandout;
